/**
 * Utility for managing pending import state during authentication flow.
 * Uses sessionStorage to persist import data when user is redirected to login/signup,
 * allowing the import to resume after authentication.
 */

export interface ParsedImportCard {
  scryfallUuid: string;
  cardName: string;
  setCode?: string;
  quantity: number;
  foilQuantity: number;
  language?: string;
  originalRow: number;
}

export interface PendingImportState {
  partner: string;
  parsedCards: ParsedImportCard[];
  selectedCondition: string;
  cardCount: number;
  sessionId?: string;
  timestamp: number;
}

const IMPORT_KEY = 'trademagic_pending_import';
const RETURN_TO_KEY = 'trademagic_return_to';
const TTL_MS = 30 * 60 * 1000; // 30 minutes

export class ImportSessionStorage {
  /**
   * Save pending import state before redirecting to login
   */
  static savePendingImport(state: Omit<PendingImportState, 'timestamp'>): void {
    try {
      const stateWithTimestamp: PendingImportState = {
        ...state,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(IMPORT_KEY, JSON.stringify(stateWithTimestamp));
    } catch (error) {
      console.error('Failed to save pending import:', error);
    }
  }

  /**
   * Retrieve pending import state after authentication
   * Returns null if expired (>30 min) or not found
   */
  static getPendingImport(): PendingImportState | null {
    try {
      const data = sessionStorage.getItem(IMPORT_KEY);
      if (!data) return null;

      const parsed: PendingImportState = JSON.parse(data);

      // Check TTL
      if (Date.now() - parsed.timestamp > TTL_MS) {
        this.clearPendingImport();
        return null;
      }

      return parsed;
    } catch (error) {
      console.error('Failed to get pending import:', error);
      return null;
    }
  }

  /**
   * Clear pending import state (call after successful import)
   */
  static clearPendingImport(): void {
    try {
      sessionStorage.removeItem(IMPORT_KEY);
    } catch (error) {
      console.error('Failed to clear pending import:', error);
    }
  }

  /**
   * Set the URL to return to after authentication
   */
  static setReturnTo(url: string): void {
    try {
      // Only allow relative URLs for security
      if (url.startsWith('/')) {
        console.log('[ImportSessionStorage] Setting returnTo:', url);
        sessionStorage.setItem(RETURN_TO_KEY, url);
      }
    } catch (error) {
      console.error('Failed to set returnTo:', error);
    }
  }

  /**
   * Get the returnTo URL (does not clear it)
   */
  static getReturnTo(): string | null {
    try {
      return sessionStorage.getItem(RETURN_TO_KEY);
    } catch (error) {
      console.error('Failed to get returnTo:', error);
      return null;
    }
  }

  /**
   * Get and clear the returnTo URL in one operation
   * Use this after successful authentication
   */
  static getAndClearReturnTo(): string | null {
    try {
      const url = sessionStorage.getItem(RETURN_TO_KEY);
      console.log('[ImportSessionStorage] Getting returnTo:', url);
      if (url) {
        sessionStorage.removeItem(RETURN_TO_KEY);
      }
      return url;
    } catch (error) {
      console.error('Failed to get and clear returnTo:', error);
      return null;
    }
  }

  /**
   * Clear returnTo URL
   */
  static clearReturnTo(): void {
    try {
      sessionStorage.removeItem(RETURN_TO_KEY);
    } catch (error) {
      console.error('Failed to clear returnTo:', error);
    }
  }

  /**
   * Check if there's a pending import
   */
  static hasPendingImport(): boolean {
    return this.getPendingImport() !== null;
  }
}

export default ImportSessionStorage;
